/**
 * Audit Viewer API Service
 *
 * Feature: 123-audit-log-viewer
 * Description: API client functions for the audit log viewer feature
 * Constitution: v1.12.0
 */

import { apiClient } from './apiClient';
import type {
  SystemAuditLogEntry,
  AuthAuditLogEntry,
  SystemAuditLogFilters,
  AuthAuditLogFilters,
  PaginationParams,
  PaginatedResponse,
} from '../types/audit';

export async function getSystemAuditLog(
  filters: SystemAuditLogFilters,
  pagination: PaginationParams
): Promise<PaginatedResponse<SystemAuditLogEntry>> {
  const params = {
    ...filters,
    page: pagination.page,
    pageSize: pagination.pageSize,
  };
  const response = await apiClient.get<PaginatedResponse<SystemAuditLogEntry>>(
    '/api/audit/system',
    { params }
  );
  return response.data;
}

export async function getSystemAuditEntry(id: string): Promise<SystemAuditLogEntry> {
  const response = await apiClient.get<SystemAuditLogEntry>(`/api/audit/system/${id}`);
  return response.data;
}

export async function getAuthAuditLog(
  filters: AuthAuditLogFilters,
  pagination: PaginationParams
): Promise<PaginatedResponse<AuthAuditLogEntry>> {
  const params = {
    ...filters,
    page: pagination.page,
    pageSize: pagination.pageSize,
  };
  const response = await apiClient.get<PaginatedResponse<AuthAuditLogEntry>>(
    '/api/audit/auth',
    { params }
  );
  return response.data;
}

export async function getAuthAuditEntry(id: number): Promise<AuthAuditLogEntry> {
  const response = await apiClient.get<AuthAuditLogEntry>(`/api/audit/auth/${id}`);
  return response.data;
}

export async function getEntityTypes(): Promise<string[]> {
  const response = await apiClient.get<{ data: string[] }>('/api/audit/system/entity-types');
  return response.data.data;
}

export async function getActions(): Promise<string[]> {
  const response = await apiClient.get<{ data: string[] }>('/api/audit/system/actions');
  return response.data.data;
}

/** Resolve a batch of entity GUIDs (from an audit entry's payload) to friendly names. Returns a
 *  lowercase-keyed map; unknown GUIDs are simply absent. */
export async function resolveNames(ids: string[]): Promise<Record<string, string>> {
  if (!ids.length) return {};
  const response = await apiClient.post<{ data: Record<string, string> }>(
    '/api/audit/system/resolve-names',
    { ids }
  );
  return response.data.data;
}

export async function exportSystemAuditCsv(
  filters: SystemAuditLogFilters
): Promise<{ blob: Blob; truncated: boolean; total: number }> {
  const response = await apiClient.get('/api/audit/system/export', {
    params: filters,
    responseType: 'blob',
  });

  return {
    blob: response.data,
    truncated: response.headers['x-export-truncated'] === 'true',
    total: parseInt(response.headers['x-export-total'] || '0', 10),
  };
}

export async function exportAuthAuditCsv(
  filters: AuthAuditLogFilters
): Promise<{ blob: Blob; truncated: boolean; total: number }> {
  const response = await apiClient.get('/api/audit/auth/export', {
    params: filters,
    responseType: 'blob',
  });

  return {
    blob: response.data,
    truncated: response.headers['x-export-truncated'] === 'true',
    total: parseInt(response.headers['x-export-total'] || '0', 10),
  };
}
